import { useEffect, useState, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Brain, CheckCircle, ChevronDown, ChevronUp, Copy, Download, Mail, Edit2, UserCheck, Lock, X, Search, FileText } from 'lucide-react'
import api from '../../services/api'
import { useAuthStore } from '../../stores/authStore'
import NotificationActionPanel from '../../components/NotificationActionPanel'
import CompliancePanel from '../../components/CompliancePanel'

const AGENT_STYLES: Record<string, { colour: string; avatar: string; label: string }> = {
  nsw_whs:     { colour: '#0052CC', avatar: '🛡️', label: 'SafeWork NSW Agent' },
  vic_whs:     { colour: '#003D8F', avatar: '🛡️', label: 'WorkSafe VIC Agent' },
  qld_whs:     { colour: '#9B1D20', avatar: '🛡️', label: 'WHSQ Agent' },
  wa_whs:      { colour: '#F7A800', avatar: '🛡️', label: 'WorkSafe WA Agent' },
  sa_whs:      { colour: '#DA291C', avatar: '🛡️', label: 'SafeWork SA Agent' },
  tas_whs:     { colour: '#00843D', avatar: '🛡️', label: 'WorkSafe TAS Agent' },
  act_whs:     { colour: '#003F72', avatar: '🛡️', label: 'WorkSafe ACT Agent' },
  nt_whs:      { colour: '#D4581C', avatar: '🛡️', label: 'NT WorkSafe Agent' },
  legal:       { colour: '#6554C0', avatar: '⚖️', label: 'Legal Agent' },
  corporate:   { colour: '#0052CC', avatar: '🏢', label: 'Corporate Safety Agent' },
  operational: { colour: '#00875A', avatar: '🦺', label: 'Operational Safety Agent' },
  quality:     { colour: '#FF8B00', avatar: '✅', label: 'Quality Agent' },
}

const SEVERITY_BADGE: Record<string, string> = {
  critical: 'badge-critical', high: 'badge-high', medium: 'badge-medium', low: 'badge-low',
}

const PROSECUTION_COLOUR: Record<string, string> = {
  none: '#00875A', low: '#00875A', possible: '#FF8B00', probable: '#DE350B', likely: '#BF2600',
}

// ── Summary extraction helpers ───────────────────────────────────────────────

function extractSeverity(stateText: string): string | null {
  const m = stateText.match(/(?:severity|risk\s+rating)[:\s]+(critical|high|medium|low)/i)
  return m ? m[1].toLowerCase() : null
}

function extractProsecutionRisk(legalText: string): string {
  if (!legalText) return '—'
  const m = legalText.match(/prosecution[^:]*:\s*(none|low|possible|probable|likely)/i)
  if (m) return m[1].charAt(0).toUpperCase() + m[1].slice(1)
  if (/unlikely|no\s+prosecution|minimal\s+risk/i.test(legalText)) return 'Low'
  if (/probable|high\s+likelihood/i.test(legalText)) return 'Probable'
  if (/\blikely\b/i.test(legalText)) return 'Likely'
  if (/possible|could\s+face|may\s+face/i.test(legalText)) return 'Possible'
  return '—'
}

function extractBullets(text: string, max = 4): string[] {
  return text
    .split('\n')
    .filter(l => /^[\s]*[-•*]|^\s*\d+\./.test(l))
    .map(l => l.replace(/^[\s\-•*\d.]+\s*/, '').replace(/\*\*/g, '').trim())
    .filter(l => l.length > 15)
    .slice(0, max)
}

function extractImmediateActions(text: string): string[] {
  if (!text) return []
  const section = text.match(/IMMEDIATE[^:]*:([\s\S]*?)(?=SHORT[-\s]TERM|SYSTEMIC|\n#{1,3}\s|\n\*{2}[A-Z]|$)/i)
  return extractBullets(section ? section[1] : text, 4)
}

// ── Sub-components ────────────────────────────────────────────────────────────

function AgentCard({ entry, defaultOpen = false }: { entry: any; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen)
  const style = AGENT_STYLES[entry.agent] || { colour: '#0052CC', avatar: '🤖', label: entry.agentName }
  return (
    <div className="rounded-xl border-l-4 overflow-hidden" style={{ borderColor: style.colour }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left"
        style={{ background: `${style.colour}10` }}
      >
        <span className="text-base">{style.avatar}</span>
        <span className="font-semibold text-sm flex-1" style={{ color: style.colour }}>
          {entry.agentName || style.label}
        </span>
        <span className="text-xs ai-label mr-2 hidden sm:inline">AI-assisted</span>
        {open ? <ChevronUp size={15} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
               : <ChevronDown size={15} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />}
      </button>
      {open && (
        <div
          className="px-4 pb-4 pt-3 text-sm whitespace-pre-wrap"
          style={{ background: `${style.colour}05`, color: 'var(--text)', lineHeight: 1.75 }}
        >
          {entry.content}
        </div>
      )}
    </div>
  )
}

function StatChip({ label, value, colour }: { label: string; value: string; colour?: string }) {
  return (
    <div className="flex flex-col items-center gap-1 px-4 py-3 rounded-xl text-center min-w-[100px]"
         style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
      <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>{label}</span>
      <span className="font-bold text-sm" style={{ color: colour || 'var(--text)' }}>{value}</span>
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export default function IncidentDetail() {
  const { id } = useParams<{ id: string }>()
  const [incident, setIncident] = useState<any>(null)
  const [debate, setDebate] = useState<any[]>([])
  const [notifStatus, setNotifStatus] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [analysing, setAnalysing] = useState(false)
  const [analysisView, setAnalysisView] = useState<'summary' | 'full'>('summary')
  const [error, setError] = useState('')

  // Edit mode
  const [editMode, setEditMode] = useState(false)
  const [editForm, setEditForm] = useState<any>({})
  const [editSaving, setEditSaving] = useState(false)

  // Lifecycle actions
  const [reviewSaving, setReviewSaving] = useState(false)
  const [showCloseForm, setShowCloseForm] = useState(false)
  const [closeNotes, setCloseNotes] = useState('')
  const [closeOutcomeStatus, setCloseOutcomeStatus] = useState('resolved')
  const [closeSaving, setCloseSaving] = useState(false)

  const { accessToken, user } = useAuthStore()
  const canReclassify = ['ops_safety', 'corp_safety', 'tenant_admin'].includes(user?.role || '')
  const [notifReportLoading, setNotifReportLoading] = useState(false)
  const [reportDownloading, setReportDownloading] = useState<string | null>(null)
  const [showReportMenu, setShowReportMenu] = useState(false)

  const downloadNotifiableReport = async () => {
    if (!incident) return
    setNotifReportLoading(true)
    try {
      const resp = await fetch(`/api/incidents/${id}/notifiable-report.pdf`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      if (!resp.ok) throw new Error()
      const blob = await resp.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a'); a.href = url; a.download = `${incident.incident_number}-notifiable-report.pdf`; a.click()
      URL.revokeObjectURL(url)
    } catch { alert('PDF generation failed. Try again.') }
    setNotifReportLoading(false)
  }

  const downloadReport = async (format: 'pdf' | 'docx') => {
    if (!incident) return
    setReportDownloading(format)
    setShowReportMenu(false)
    try {
      const resp = await fetch(`/api/incidents/${id}/report.${format}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      if (!resp.ok) throw new Error()
      const blob = await resp.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${incident.incident_number}-report.${format}`
      a.click()
      URL.revokeObjectURL(url)
    } catch { alert('Report generation failed. Try again.') }
    setReportDownloading(null)
  }

  // Reclassify (ops_safety+)
  const [showReclassify, setShowReclassify] = useState(false)
  const [reclassifyNotifiable, setReclassifyNotifiable] = useState<boolean | null>(null)
  const [reclassifyReason, setReclassifyReason] = useState('')
  const [reclassifySaving, setReclassifySaving] = useState(false)
  const [reclassifyError, setReclassifyError] = useState('')

  const submitReclassify = async () => {
    if (reclassifyNotifiable === null || !reclassifyReason.trim()) return
    setReclassifySaving(true); setReclassifyError('')
    try {
      await api.patch(`/incidents/${id}/reclassify`, { notifiable: reclassifyNotifiable, reason: reclassifyReason })
      setShowReclassify(false); setReclassifyReason(''); setReclassifyNotifiable(null)
      await load()
    } catch (e: any) {
      setReclassifyError(e.response?.data?.error || 'Failed to reclassify')
    }
    setReclassifySaving(false)
  }

  // Notification compilation
  const [notifAudience, setNotifAudience] = useState<'customer' | 'elt' | 'board' | null>(null)
  const [notifDraft, setNotifDraft] = useState<{ subject: string; body: string } | null>(null)
  const [notifLoading, setNotifLoading] = useState(false)
  const [notifError, setNotifError] = useState('')
  const [copied, setCopied] = useState(false)

  const load = async () => {
    try {
      const [incRes, notifRes] = await Promise.all([
        api.get(`/incidents/${id}`),
        api.get(`/incidents/${id}/notification-status`),
      ])
      setIncident(incRes.data)
      setNotifStatus(notifRes.data)
      if (incRes.data.debate_transcript) {
        try { setDebate(JSON.parse(incRes.data.debate_transcript)) } catch {}
      }
    } catch { setError('Failed to load incident') }
    setLoading(false)
  }

  useEffect(() => { load() }, [id])

  const runAnalysis = async () => {
    setAnalysing(true)
    setError('')
    try {
      const { data } = await api.post(`/incidents/${id}/analyse`, {})
      setDebate(data.debate || [])
      setNotifStatus({
        notifiable: data.notifiable,
        phone: { due: data.notification_phone_due, completed: null,
          overdue: false, remaining_seconds: data.notification_phone_due ? 3600 : null },
        written: { due: data.notification_written_due, completed: null,
          overdue: false, remaining_seconds: data.notification_written_due ? 172800 : null },
      })
      await load()
    } catch (err: any) {
      setError(err.response?.data?.message || 'Analysis failed')
    }
    setAnalysing(false)
  }

  const startEdit = () => {
    setEditForm({
      description: incident.description,
      location_detail: incident.location_detail || '',
      activity_at_time: incident.activity_at_time || '',
      injury_type: incident.injury_type || '',
      days_lost: incident.days_lost || 0,
    })
    setEditMode(true)
  }

  const saveEdit = async () => {
    setEditSaving(true)
    setError('')
    try {
      const { data } = await api.patch(`/incidents/${id}`, editForm)
      setIncident((prev: any) => ({ ...prev, ...data }))
      setEditMode(false)
    } catch (err: any) {
      setError(err.response?.data?.error || 'Save failed')
    }
    setEditSaving(false)
  }

  const supervisorReview = async () => {
    setReviewSaving(true)
    setError('')
    try {
      const { data } = await api.post(`/incidents/${id}/supervisor-review`, {})
      setIncident((prev: any) => ({ ...prev, ...data }))
    } catch (err: any) {
      setError(err.response?.data?.error || 'Sign-off failed')
    }
    setReviewSaving(false)
  }

  const closeIncident = async () => {
    if (!closeNotes.trim()) return
    setCloseSaving(true)
    setError('')
    try {
      const { data } = await api.post(`/incidents/${id}/close`, {
        outcome_notes: closeNotes,
        outcome_status: closeOutcomeStatus,
      })
      setIncident((prev: any) => ({ ...prev, ...data }))
      setShowCloseForm(false)
      setCloseNotes('')
    } catch (err: any) {
      setError(err.response?.data?.error || 'Close failed')
    }
    setCloseSaving(false)
  }

  const compileNotif = async (audience: 'customer' | 'elt' | 'board') => {
    if (notifAudience === audience && notifDraft) return  // already loaded
    setNotifAudience(audience)
    setNotifDraft(null)
    setNotifError('')
    setNotifLoading(true)
    try {
      const { data } = await api.post(`/incidents/${id}/compile-notification`, { audience })
      setNotifDraft({ subject: data.subject, body: data.body })
    } catch (err: any) {
      setNotifError(err?.response?.data?.message || err?.response?.data?.error || 'Compilation failed')
    }
    setNotifLoading(false)
  }

  const copyDraft = async () => {
    if (!notifDraft) return
    await navigator.clipboard.writeText(`Subject: ${notifDraft.subject}\n\n${notifDraft.body}`)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const exportDraft = () => {
    if (!notifDraft || !incident) return
    const content = `INCIDENT: ${incident.incident_number}\nDate: ${new Date(incident.date_occurred).toLocaleDateString('en-AU')}\n\nSubject: ${notifDraft.subject}\n\n${notifDraft.body}\n\n---\nAI-assisted draft — requires human approval before sending`
    const blob = new Blob([content], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${incident.incident_number}-${notifAudience}-notification.txt`
    a.click()
    URL.revokeObjectURL(url)
  }

  // Derived summary data from debate
  const stateEntry = useMemo(() => debate.find(d => d.round === 1), [debate])
  const legalEntry = useMemo(() => debate.find(d => d.agent === 'legal'), [debate])
  const operationalEntry = useMemo(() => debate.find(d => d.agent === 'operational'), [debate])

  const detectedSeverity = useMemo(() =>
    extractSeverity(stateEntry?.content || ''), [stateEntry])
  const prosecutionRisk = useMemo(() =>
    extractProsecutionRisk(legalEntry?.content || ''), [legalEntry])
  const immediateActions = useMemo(() =>
    extractImmediateActions(operationalEntry?.content || ''), [operationalEntry])
  const keyFindings = useMemo(() =>
    extractBullets(stateEntry?.content || '', 3), [stateEntry])

  const displaySeverity = (incident?.ai_severity || detectedSeverity || '').toLowerCase()

  if (loading) return <div className="text-center py-16" style={{ color: 'var(--text-muted)' }}>Loading...</div>
  if (error && !incident) return <div className="text-center py-16" style={{ color: 'var(--red)' }}>{error}</div>
  if (!incident) return null

  return (
    <div className="max-w-4xl mx-auto space-y-6">

      {/* ── Header ── */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <Link to="/incidents" className="text-sm" style={{ color: 'var(--accent)' }}>← Incidents</Link>
          <h1 className="text-2xl font-bold mt-1" style={{ color: 'var(--text)' }}>{incident.incident_number}</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>
            {incident.category?.replace('_', ' ')} · {incident.site_name || 'Unknown site'} ·{' '}
            {new Date(incident.date_occurred).toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {displaySeverity && <span className={`badge ${SEVERITY_BADGE[displaySeverity] || ''} capitalize`}>{displaySeverity}</span>}
          <span className={`badge capitalize ${
            incident.status === 'open' ? 'badge-high' :
            incident.status === 'in_progress' ? 'badge-medium' : 'badge-low'
          }`}>
            {incident.status?.replace('_', ' ')}
          </span>
        </div>
      </div>

      {/* ── Notifiable Compliance Action Panel ── */}
      {incident.notifiable === 1 && (
        <NotificationActionPanel incident={incident} onUpdate={load} />
      )}

      {/* ── Compliance Panel — ONLY for legally notifiable incidents (WHS Act s35/s36) ── */}
      {incident.notifiable === 1 && (
        <CompliancePanel incident={incident} onRefresh={load} />
      )}

      {/* ── Recordable classification + reclassify option ── */}
      {(incident.recordable_category || canReclassify) && (
        <div className="card" style={{ padding: '0.75rem 1.25rem' }}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3 flex-wrap">
              {incident.recordable_category && (
                <span className={`badge ${incident.recordable_category === 'LTI' ? 'badge-high' : incident.recordable_category === 'MTI' ? 'badge-medium' : 'badge-low'}`}>
                  {incident.recordable_category}
                </span>
              )}
              {incident.notifiable === 1 ? (
                <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: '#FFEBE6', color: '#D93025' }}>NOTIFIABLE</span>
              ) : (
                <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ background: 'var(--bg)', color: 'var(--text-muted)', border: '1px solid var(--border)' }}>
                  Not Notifiable — internal management
                </span>
              )}
              {incident.notifiable_override && (
                <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  (manually reclassified · {incident.notifiable_override_reason})
                </span>
              )}
            </div>
            {canReclassify && (
              <button type="button" onClick={() => setShowReclassify(r => !r)}
                className="text-xs font-medium" style={{ color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                Reclassify
              </button>
            )}
          </div>

          {showReclassify && (
            <div className="mt-4 pt-4 space-y-3" style={{ borderTop: '1px solid var(--border)' }}>
              <p className="text-xs font-semibold" style={{ color: 'var(--text)' }}>Change notifiable classification (logged to audit trail)</p>
              <div className="flex gap-2">
                {[{ v: true, l: 'Notifiable' }, { v: false, l: 'Not Notifiable' }].map(({ v, l }) => (
                  <button key={l} type="button" onClick={() => setReclassifyNotifiable(v)}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium border"
                    style={{
                      background: reclassifyNotifiable === v ? (v ? '#FFEBE6' : 'var(--accent-light)') : 'var(--bg)',
                      color: reclassifyNotifiable === v ? (v ? '#D93025' : 'var(--accent)') : 'var(--text-muted)',
                      border: reclassifyNotifiable === v ? `2px solid ${v ? '#D93025' : 'var(--accent)'}` : '1px solid var(--border)',
                    }}>{l}</button>
                ))}
              </div>
              <div>
                <label className="text-xs font-medium block mb-1" style={{ color: 'var(--text-secondary)' }}>Reason for reclassification *</label>
                <textarea value={reclassifyReason} onChange={e => setReclassifyReason(e.target.value)} rows={2}
                  placeholder="e.g. Reviewed with SafeWork — dangerous incident criteria met / does not meet s35 criteria because…" />
              </div>
              {reclassifyError && <p className="text-xs" style={{ color: 'var(--red)' }}>{reclassifyError}</p>}
              <div className="flex gap-2">
                <button type="button" onClick={submitReclassify} disabled={reclassifySaving || reclassifyNotifiable === null || !reclassifyReason.trim()}
                  className="btn btn-primary text-xs">
                  {reclassifySaving ? 'Saving…' : 'Save Reclassification'}
                </button>
                <button type="button" onClick={() => { setShowReclassify(false); setReclassifyError('') }} className="btn btn-secondary text-xs">Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Description ── */}
      {!editMode ? (
        <div className="card space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold" style={{ color: 'var(--text)' }}>Description</h2>
            {incident.status !== 'closed' && (
              <button type="button" onClick={startEdit} className="btn btn-secondary text-xs py-1.5 flex items-center gap-1.5">
                <Edit2 size={13} /> Edit
              </button>
            )}
          </div>
          <p style={{ color: 'var(--text)', lineHeight: 1.7 }}>{incident.description}</p>
          {incident.activity_at_time && (
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}><strong>Activity:</strong> {incident.activity_at_time}</p>
          )}
          {incident.location_detail && (
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}><strong>Location:</strong> {incident.location_detail}</p>
          )}
          {incident.injury_type && (
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}><strong>Injury:</strong> {incident.injury_type} · Days lost: {incident.days_lost || 0}</p>
          )}
        </div>
      ) : (
        <div className="card space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold" style={{ color: 'var(--text)' }}>Edit Incident</h2>
            <button type="button" onClick={() => setEditMode(false)} className="text-sm" style={{ color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer' }}>
              <X size={16} />
            </button>
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Description *</label>
            <textarea
              rows={4}
              value={editForm.description}
              onChange={e => setEditForm((f: any) => ({ ...f, description: e.target.value }))}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Location</label>
              <input value={editForm.location_detail} onChange={e => setEditForm((f: any) => ({ ...f, location_detail: e.target.value }))} />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Activity at Time</label>
              <input value={editForm.activity_at_time} onChange={e => setEditForm((f: any) => ({ ...f, activity_at_time: e.target.value }))} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Injury Type</label>
              <input value={editForm.injury_type} onChange={e => setEditForm((f: any) => ({ ...f, injury_type: e.target.value }))} />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Days Lost</label>
              <input type="number" min={0} value={editForm.days_lost} onChange={e => setEditForm((f: any) => ({ ...f, days_lost: parseInt(e.target.value) || 0 }))} />
            </div>
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={saveEdit} disabled={editSaving || !editForm.description?.trim()} className="btn btn-primary">
              {editSaving ? 'Saving...' : 'Save Changes'}
            </button>
            <button type="button" onClick={() => setEditMode(false)} className="btn btn-secondary">Cancel</button>
          </div>
        </div>
      )}

      {/* ── Lifecycle ── */}
      <div className="card space-y-4">
        {/* Status stepper */}
        <div className="flex items-center gap-2">
          {(['open', 'in_progress', 'closed'] as const).map((s, i, arr) => {
            const labels: Record<string, string> = { open: 'Open', in_progress: 'In Progress', closed: 'Closed' }
            const colours: Record<string, string> = { open: 'var(--red)', in_progress: 'var(--orange, #FF8B00)', closed: 'var(--green, #00875A)' }
            const isActive = incident.status === s
            const isPast = arr.indexOf(incident.status) > i
            return (
              <div key={s} className="flex items-center gap-2">
                <div
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold"
                  style={{
                    background: isActive || isPast ? colours[s] + '20' : 'var(--bg)',
                    color: isActive || isPast ? colours[s] : 'var(--text-muted)',
                    border: `1px solid ${isActive || isPast ? colours[s] : 'var(--border)'}`,
                  }}
                >
                  {isPast && <CheckCircle size={12} />}
                  {isActive && <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: colours[s] }} />}
                  {labels[s]}
                </div>
                {i < arr.length - 1 && (
                  <span style={{ color: 'var(--border)' }}>›</span>
                )}
              </div>
            )
          })}
        </div>

        {/* Supervisor sign-off info */}
        {incident.supervisor_reviewed_at && (
          <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
            <UserCheck size={15} style={{ color: 'var(--green, #00875A)', flexShrink: 0 }} />
            Supervisor reviewed by <strong>{incident.supervisor_name || 'Unknown'}</strong> on{' '}
            {new Date(incident.supervisor_reviewed_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
          </div>
        )}

        {/* Outcome notes (when closed) */}
        {incident.status === 'closed' && incident.outcome_notes && (
          <div className="rounded-xl p-4 space-y-1" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
            <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
              Outcome — {incident.outcome_status || 'resolved'}
            </p>
            <p className="text-sm" style={{ color: 'var(--text)', lineHeight: 1.6 }}>{incident.outcome_notes}</p>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex flex-wrap gap-3 items-start">
          <Link
            to={`/investigations/new?incident_id=${id}`}
            className="btn btn-secondary flex items-center gap-2 text-sm"
          >
            <Search size={14} /> Start Investigation
          </Link>

          {incident.status !== 'closed' && !incident.supervisor_reviewed_at && (
            <button
              type="button"
              onClick={supervisorReview}
              disabled={reviewSaving}
              className="btn btn-secondary flex items-center gap-2"
            >
              <UserCheck size={15} />
              {reviewSaving ? 'Signing off...' : 'Supervisor Sign-off'}
            </button>
          )}

          {incident.status !== 'closed' && !showCloseForm && (
            <button
              type="button"
              onClick={() => setShowCloseForm(true)}
              className="btn flex items-center gap-2"
              style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)' }}
            >
              <Lock size={14} />
              Close Incident
            </button>
          )}

          {incident.notifiable === 1 && (
            <button
              type="button"
              onClick={downloadNotifiableReport}
              disabled={notifReportLoading}
              className="btn btn-secondary flex items-center gap-2"
              title="Generate SafeWork NSW Form 10 style notifiable incident report"
            >
              <FileText size={14} />
              {notifReportLoading ? 'Generating…' : 'Notifiable Report PDF'}
            </button>
          )}

          {/* Download Report dropdown */}
          <div style={{ position: 'relative' }}>
            <button
              type="button"
              onClick={() => setShowReportMenu(m => !m)}
              disabled={!!reportDownloading}
              className="btn btn-secondary flex items-center gap-2"
            >
              <Download size={14} />
              {reportDownloading ? `Generating ${reportDownloading.toUpperCase()}…` : 'Download Report'}
            </button>
            {showReportMenu && (
              <div
                style={{
                  position: 'absolute', top: '100%', right: 0, marginTop: 4,
                  background: 'var(--surface)', border: '1px solid var(--border)',
                  borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                  minWidth: 160, zIndex: 50,
                }}
                onMouseLeave={() => setShowReportMenu(false)}
              >
                <button
                  onClick={() => downloadReport('pdf')}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--text)' }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                >
                  <FileText size={14} style={{ color: 'var(--red)' }} /> PDF Report
                </button>
                <div style={{ height: 1, background: 'var(--border)', margin: '0 8px' }} />
                <button
                  onClick={() => downloadReport('docx')}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--text)' }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                >
                  <FileText size={14} style={{ color: 'var(--blue)' }} /> Word Document
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Inline close form */}
        {showCloseForm && (
          <div className="space-y-3 pt-2" style={{ borderTop: '1px solid var(--border)' }}>
            <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>Close Incident</p>
            <div>
              <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Outcome Notes *</label>
              <textarea
                rows={3}
                placeholder="Describe what corrective actions were taken and how the incident was resolved..."
                value={closeNotes}
                onChange={e => setCloseNotes(e.target.value)}
                autoFocus
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Outcome Status</label>
              <select value={closeOutcomeStatus} onChange={e => setCloseOutcomeStatus(e.target.value)} className="w-auto">
                <option value="resolved">Resolved</option>
                <option value="resolved_with_actions">Resolved with ongoing actions</option>
                <option value="escalated">Escalated</option>
                <option value="no_further_action">No further action required</option>
              </select>
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={closeIncident}
                disabled={closeSaving || !closeNotes.trim()}
                className="btn btn-danger flex items-center gap-2"
              >
                <Lock size={14} />
                {closeSaving ? 'Closing...' : 'Confirm Close'}
              </button>
              <button type="button" onClick={() => setShowCloseForm(false)} className="btn btn-secondary">Cancel</button>
            </div>
          </div>
        )}
      </div>

      {/* ── AI Analysis ── */}
      <div className="card space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Brain size={20} style={{ color: 'var(--purple)' }} />
            <h2 className="font-semibold" style={{ color: 'var(--text)' }}>AI Agent Analysis</h2>
          </div>
          <div className="flex items-center gap-2">
            {debate.length > 0 && (
              <button
                type="button"
                onClick={() => setAnalysisView(v => v === 'summary' ? 'full' : 'summary')}
                className="btn btn-secondary text-xs py-1.5"
              >
                {analysisView === 'summary' ? 'View Full Analysis ↓' : 'Back to Summary ↑'}
              </button>
            )}
            <button onClick={runAnalysis} disabled={analysing} className="btn btn-secondary">
              {analysing ? 'Analysing...' : debate.length > 0 ? 'Re-analyse' : 'Run AI Analysis'}
            </button>
          </div>
        </div>

        {error && (
          <div className="text-sm p-3 rounded-lg" style={{ background: 'var(--red-light)', color: 'var(--red)' }}>{error}</div>
        )}

        {/* Loading shimmer */}
        {analysing && (
          <div className="space-y-3">
            {['State WHS Agent analysing legislation…', 'Legal Agent assessing exposure…', 'Corporate Agent reviewing governance…', 'Operational Agent preparing actions…'].map((msg, i) => (
              <div key={i} className="flex items-center gap-3 text-sm p-3 rounded-lg animate-pulse" style={{ background: 'var(--bg)' }}>
                <Brain size={16} style={{ color: 'var(--purple)' }} />
                <span style={{ color: 'var(--text-secondary)' }}>{msg}</span>
              </div>
            ))}
          </div>
        )}

        {/* Empty state */}
        {debate.length === 0 && !analysing && (
          <div className="text-center py-8 text-sm" style={{ color: 'var(--text-muted)' }}>
            Click "Run AI Analysis" to get a 5-agent WHS assessment of this incident
          </div>
        )}

        {/* ── Summary view (default) ── */}
        {debate.length > 0 && !analysing && analysisView === 'summary' && (
          <div className="space-y-4">
            {/* Stats row */}
            <div className="flex flex-wrap gap-3">
              {displaySeverity && (
                <StatChip
                  label="Severity"
                  value={displaySeverity.toUpperCase()}
                  colour={displaySeverity === 'critical' ? '#BF2600' : displaySeverity === 'high' ? '#DE350B' : displaySeverity === 'medium' ? '#FF8B00' : '#00875A'}
                />
              )}
              <StatChip
                label="Notifiable"
                value={notifStatus?.notifiable ? 'YES' : 'NO'}
                colour={notifStatus?.notifiable ? '#DE350B' : '#00875A'}
              />
              {prosecutionRisk !== '—' && (
                <StatChip
                  label="Prosecution Risk"
                  value={prosecutionRisk}
                  colour={PROSECUTION_COLOUR[prosecutionRisk.toLowerCase()] || 'var(--text)'}
                />
              )}
            </div>

            {/* Findings + Actions grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {keyFindings.length > 0 && (
                <div className="rounded-xl p-4 space-y-2" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
                  <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Key Findings</p>
                  <ul className="space-y-1.5">
                    {keyFindings.map((f, i) => (
                      <li key={i} className="text-sm flex gap-2" style={{ color: 'var(--text)' }}>
                        <span style={{ color: 'var(--accent)', flexShrink: 0 }}>•</span>
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {immediateActions.length > 0 && (
                <div className="rounded-xl p-4 space-y-2" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
                  <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Immediate Actions (24h)</p>
                  <ul className="space-y-1.5">
                    {immediateActions.map((a, i) => (
                      <li key={i} className="text-sm flex gap-2" style={{ color: 'var(--text)' }}>
                        <span style={{ color: 'var(--red)', flexShrink: 0 }}>→</span>
                        <span>{a}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="ai-label text-center text-xs">
              AI-assisted analysis — all recommendations require human approval (NSW Digital Work Systems Act 2026)
            </div>
          </div>
        )}

        {/* ── Full debate view ── */}
        {debate.length > 0 && !analysing && analysisView === 'full' && (
          <div className="space-y-3">
            <div className="ai-label text-center text-xs">
              AI-assisted analysis — all recommendations require human approval (NSW Digital Work Systems Act 2026)
            </div>
            {debate.map((entry, i) => (
              <AgentCard key={i} entry={entry} defaultOpen={i === 0} />
            ))}
          </div>
        )}
      </div>

      {/* ── Compile Notifications ── */}
      {debate.length > 0 && (
        <div className="card space-y-4">
          <div className="flex items-center gap-2">
            <Mail size={20} style={{ color: 'var(--accent)' }} />
            <h2 className="font-semibold" style={{ color: 'var(--text)' }}>Compile Notification</h2>
          </div>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            Generate a tailored notification for your chosen audience. AI drafts — review before sending.
          </p>

          {/* Audience selector */}
          <div className="flex gap-2 flex-wrap">
            {(['customer', 'elt', 'board'] as const).map(aud => (
              <button
                key={aud}
                type="button"
                onClick={() => compileNotif(aud)}
                disabled={notifLoading}
                className={`btn text-sm capitalize ${notifAudience === aud ? 'btn-primary' : 'btn-secondary'}`}
              >
                {aud === 'elt' ? 'ELT' : aud.charAt(0).toUpperCase() + aud.slice(1)}
              </button>
            ))}
          </div>

          {/* Draft output */}
          {notifLoading && (
            <div className="text-sm p-4 rounded-xl animate-pulse" style={{ background: 'var(--bg)', color: 'var(--text-muted)' }}>
              Compiling {notifAudience?.toUpperCase()} notification…
            </div>
          )}

          {notifError && (
            <div className="text-sm p-3 rounded-lg" style={{ background: 'var(--red-light)', color: 'var(--red)' }}>{notifError}</div>
          )}

          {notifDraft && !notifLoading && (
            <div className="space-y-3">
              <div
                className="rounded-xl p-4 space-y-3 text-sm"
                style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}
              >
                <div className="pb-3" style={{ borderBottom: '1px solid var(--border)' }}>
                  <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Subject</span>
                  <p className="font-semibold mt-0.5" style={{ color: 'var(--text)' }}>{notifDraft.subject}</p>
                </div>
                <div
                  className="whitespace-pre-wrap"
                  style={{ color: 'var(--text)', lineHeight: 1.75 }}
                >
                  {notifDraft.body}
                </div>
              </div>

              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="ai-label text-xs">AI-assisted draft — requires human approval before sending</div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={copyDraft}
                    className="btn btn-secondary text-xs py-1.5 flex items-center gap-1.5"
                  >
                    <Copy size={13} />
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                  <button
                    type="button"
                    onClick={exportDraft}
                    className="btn btn-secondary text-xs py-1.5 flex items-center gap-1.5"
                  >
                    <Download size={13} />
                    Export .txt
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

    </div>
  )
}
